import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    ColorSliderDynamicStyleType,
    ColorSliderColors
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    webStyle
} from "../../utils";

export const COLOR_SLIDER_VERTICAL_LENGTH_STEPS = 4;

export const getColorSliderColors = ({
    isDisabled,
    colors
}: {
    colors: NCoreUIKit.ActivePalette["colors"];
    isDisabled: boolean;
}): ColorSliderColors => {
    const disabledColor = colors.system.state.content.disabled.neutral;

    return {
        trackColor: isDisabled ? colors.system.state.border.disabled.neutral : colors.content.container.subtle,
        edgeColor: isDisabled ? colors.system.state.border.disabled.neutral : colors.content.border.default,
        labelColor: isDisabled ? disabledColor : colors.content.text.high,
        valueColor: isDisabled ? disabledColor : colors.content.text.mid,
        hintColor: isDisabled ? disabledColor : colors.content.text.mid,
        ringColor: colors.content.container.default
    };
};

const stylesheet = StyleSheet.create({
    disabled: {
        ...webStyle({
            cursor: "not-allowed"
        })
    },
    horizontalRail: {
        ...webStyle({
            touchAction: "pan-y"
        })
    },
    verticalRail: {
        flexGrow: 1,
        ...webStyle({
            touchAction: "pan-x"
        })
    },
    head: {
        alignItems: "baseline",
        flexDirection: "row"
    },
    container: {
        ...webStyle({
            userSelect: "none"
        })
    },
    track: {
        pointerEvents: "none",
        position: "absolute",
        overflow: "hidden"
    },
    enabled: {
        ...webStyle({
            cursor: "pointer"
        })
    },
    edge: {
        position: "absolute",
        bottom: 0,
        start: 0,
        end: 0,
        top: 0
    },
    verticalField: {
        alignItems: "center"
    },
    value: {
        fontVariant: [
            "tabular-nums"
        ],
        flexShrink: 0
    },
    label: {
        flexShrink: 1,
        flexGrow: 1
    },
    horizontalTrack: {
        start: 0,
        end: 0
    },
    verticalTrack: {
        bottom: 0,
        top: 0
    }
});

export const useStyles = ({
    currentMeasures,
    controlHeights,
    currentColors,
    shapeConfigs,
    orientation,
    radiuses,
    borders,
    spaces
}: ColorSliderDynamicStyleType) => {
    const trackOffset = (currentMeasures.row - currentMeasures.trackThickness) / 2;

    const styles = {
        track: {
            backgroundColor: currentColors.trackColor,
            ...getCornerStyle({
                radius: radiuses.full,
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        edge: {
            borderColor: currentColors.edgeColor,
            borderWidth: borders.line,
            ...getCornerStyle({
                radius: radiuses.full,
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        container: {} as Mutable<ViewStyle>,
        field: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        head: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>
    };

    if(orientation === "vertical") {
        Object.assign(styles.container, {
            minHeight: controlHeights.medium * COLOR_SLIDER_VERTICAL_LENGTH_STEPS,
            width: currentMeasures.row
        });

        Object.assign(styles.track, {
            width: currentMeasures.trackThickness,
            start: trackOffset
        });

        return styles;
    }

    Object.assign(styles.container, {
        height: currentMeasures.row
    });

    Object.assign(styles.track, {
        height: currentMeasures.trackThickness,
        top: trackOffset
    });

    return styles;
};
export default stylesheet;
